import { call, get, sync } from 'vuex-pathify';

import { GeoJSON } from 'ol/format';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Style, Fill, Stroke, Circle, Icon } from 'ol/style';
import { Draw, Modify, Snap } from 'ol/interaction';
import { unByKey } from 'ol/Observable';
import { prepareDrawnGeometry } from '@/assets/js/mapUtils';
import WKT from 'ol/format/WKT';
import { buffer } from 'ol/extent';
import Feature from 'ol/Feature';
import { fromExtent } from 'ol/geom/Polygon';

import { minEditingZoom, defaultMinZoom, defaultEpsg } from '@/assets/js/variables';

let CABLE_SNAPPING_PROXY_LISTENERS = [];
const PROXY_SOURCE = new VectorSource();
let snappingKey = undefined;

export default {
  computed: {
    parcelDataSource: get('admin/modulesMapping@parcels.parcels_datasource'),
    settingsParcelsProvider: get('admin/settingsValues@parcels_provider'),
    isModificationInteractionActive: sync('edit/isModificationInteractionActive'),
    isDrawingInteractionActive: sync('edit/isDrawingInteractionActive'),
    isModifySnappingActive: sync('edit/isModifySnappingActive'),
    cableDataSourceName: get('admin/modulesMapping@zdm_data.datasources.cable.datasource'),
    streetLampDataSourceName: get('admin/modulesMapping@zdm_data.datasources.street_lamp.datasource'),
    globalFilters: get('edit/globalFilters'),
  },
  methods: {
    getFeature: call('layers/getDataSourceFeature'),
    getLayersFeaturesInBbox: call('layers/getLayersFeaturesInBbox'),
    getLayerFeature: call('layers/getLayerFeature'),
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    searchUldkParcelByXY: call('search/searchUldkParcelByXY'),
    registerSidebarGeometryListeners() {
      this.$root.$on('assignedCoordinates', this.assignGeometry);
      this.$root.$on('zoomSidebarGeometry', this.zoomSidebarGeometry);
      this.$root.$on('clearSidebarGeometry', this.clearSidebarGeometry);
      this.$root.$on('deleteSidebarGeometry', this.deleteSidebarGeometry);
      this.$root.$on('drawSidebarGeometry', this.initDrawingSidebarGeometry);
      this.$root.$on('modifySidebarGeometry', this.initModifySidebarGeometry);
      this.$root.$on('deactivateSidebarModify', this.deactivateSidebarModify);
    },
    turnOffModifySnapping() {
      this.setMinZoom(defaultMinZoom);
      this.$root.$emit('toggleSnappingInteraction', { value: false });
      this.isModifySnappingActive = false;
      unByKey(snappingKey);
    },
    initModifySnapping() {
      const isZoom = this.map.getView().getZoom() < minEditingZoom;
      if (isZoom) {
        this.setView({ zoom: minEditingZoom });
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('map.zoomedToMinEditingZoom'),
        });
      }
      this.setMinZoom(minEditingZoom);
      const snappingSource = new VectorSource({
        name: 'snappingSource',
      });
      this.createSnappingInteraction(snappingSource);
      this.attachSnappingLoader(snappingSource, isZoom);
      this.isModifySnappingActive = true;
    },
    async attachSnappingLoader(source, isZoom) {
      const activeLayerId = this.layerId;
      const visibleLayersIds = this.projectLayers
        .filter(layer => layer.visible && layer.id !== activeLayerId && layer.type === 'features_layer')
        .map(layer => layer.id);
      // wrappnięta funkcja setSnappingFeatures, żeby nie pisać dwa razy tego samego
      const snappingFunctionHandler = extent => {
        return this.setSnappingFeatures(
          source,
          visibleLayersIds,
          activeLayerId,
          this.getExtentWithBuffer(extent, 100),
          this.globalFilters
        );
      };
      // nie trzeba było przybliżać (więc nie wywoła się event moveend), czyli od razu pobieramy ficzery w bboxie
      if (!isZoom) {
        snappingFunctionHandler(this.map.getView().calculateExtent());
      }
      // przypinamy listener na event moveend, żeby pobrać ficzery w bboxie
      snappingKey = this.map.on('moveend', e => {
        snappingFunctionHandler(e.map.getView().calculateExtent());
      });
    },
    async setSnappingFeatures(source, ids, activeLayerId, extent, activeFeatureFilter) {
      const featuresCollection = await this.getSnappingGeoJsonFeatures(ids, activeLayerId, extent, activeFeatureFilter);
      source.clear(true);
      source.addFeatures(
        new GeoJSON().readFeatures(featuresCollection, {
          featureProjection: featuresCollection.crs.properties.name,
          dataProjection: defaultEpsg,
        })
      );
    },
    getExtentWithBuffer(extent, bufferValue = 100) {
      return buffer(extent, bufferValue);
    },
    async getSnappingGeoJsonFeatures(ids, activeLayerId, extent, activeFeatureFilter) {
      try {
        const r = await this.getLayersFeaturesInBbox({
          active_features_filter: activeFeatureFilter,
          active_layer_id: activeLayerId,
          bbox: prepareDrawnGeometry(new Feature({ geometry: fromExtent(extent) })),
          ids,
        });
        return r.features;
      } catch (error) {
        console.log('  error', error);
      }
    },
    async assignGeometry(coordinates, name, isTurnOffOnFail = true) {
      const isNewPolygonFeatureGeometry = name === 'newPolygonFeatureGeometry';
      if (this.settingsParcelsProvider === 'uldk') {
        const payload = {
          x: coordinates[0],
          y: coordinates[1],
          epsg: this.$_config.defaultEpsg.split(':')[1],
          params: ['geom_wkt'],
        };
        const r = await this.searchUldkParcelByXY(payload);
        if (r !== null && r.error) {
          this.$store.set('snackbar/PUSH_MESSAGE!', {
            message: this.$i18n.t('snackbar.notFoundPlotWithError', { error: r.error }),
          });
          if (isTurnOffOnFail) {
            this.$root.$emit('identification-action', false);
            if (isNewPolygonFeatureGeometry) {
              this.activeTool = 'moduleIdentification';
            }
          }
          return;
        } else if (Object.keys(r).length < 1) {
          this.$store.set('snackbar/PUSH_MESSAGE!', {
            message: this.$i18n.t('snackbar.notFoundPlot'),
          });
          if (isTurnOffOnFail) {
            this.$root.$emit('identification-action', false);
            if (isNewPolygonFeatureGeometry) {
              this.activeTool = 'moduleIdentification';
            }
          }
          return;
        }
        let wktText = r.geom_wkt;
        if (wktText.indexOf(';') >= 0) {
          wktText = wktText.split(';')[1];
        }
        const feature = new WKT().readFeature(wktText, {
          dataProjection: 'EPSG:2180',
          featureProjection: this.$_config.defaultEpsg,
        });
        const geoJsonFeature = new GeoJSON().writeFeatureObject(feature, {
          featureProjection: this.$_config.defaultEpsg || 'EPSG:4326',
          dataProjection: this.$_config.defaultEpsg || 'EPSG:4326',
        });
        geoJsonFeature.geometry.crs = {
          properties: {
            name: this.$_config.defaultEpsg || 'EPSG:4326',
          },
          type: 'name',
        };
        this.$root.$emit('identification-action', false);
        if (isNewPolygonFeatureGeometry) {
          this.activeTool = 'moduleIdentification';
        }
        this.dettachCursorMoveHandler();
        this.$root.$emit('deleteSidebarGeometry');
        this.initModifySidebarGeometry({
          modifyendCallback: e => {
            this.emitSidebarGeometry(e, name);
          },
          isInitSnapping: false,
          geometryValue: geoJsonFeature.geometry,
        });
        this.$root.$emit('addedSidebarGeometry', {
          inputName: name,
          geometry: geoJsonFeature.geometry,
        });
      } else {
        const point = {
          type: 'Point',
          crs: {
            type: 'name',
            properties: {
              name: this.$_config.defaultEpsg || 'EPSG:4326',
            },
          },
          coordinates: coordinates,
        };
        const r = await this.getDataSourceFeatures({
          dataSource: this.parcelDataSource,
          filters: {
            '%SPATIALLY_INTERSECTS': {
              [`${this.parcelDataSource}.geom`]: [point],
            },
          },
        });
        if (r.features.length === 0) {
          this.$store.set('snackbar/PUSH_MESSAGE!', {
            message: this.$i18n.t('snackbar.notFoundPlot'),
          });
          if (isTurnOffOnFail) {
            this.$root.$emit('identification-action', false);
            if (isNewPolygonFeatureGeometry) {
              this.activeTool = 'moduleIdentification';
            }
          }
          return;
        } else {
          this.$root.$emit('identification-action', false);
          if (isNewPolygonFeatureGeometry) {
            this.activeTool = 'moduleIdentification';
          }
          this.dettachCursorMoveHandler();
          const geometry = {
            coordinates: r.features[0].geometry.coordinates,
            crs: r.crs,
            type: r.features[0].geometry.type,
          };
          this.initModifySidebarGeometry({
            modifyendCallback: e => {
              this.emitSidebarGeometry(e, name);
            },
            isInitSnapping: false,
            geometryValue: geometry,
          });
          this.$root.$emit('addedSidebarGeometry', {
            inputName: name,
            geometry,
          });
        }
      }
    },
    deactivateSidebarModify() {
      const interaction = this.getInteractionByName('modifySidebarInteraction');
      interaction && interaction.setActive(false);
      this.isModificationInteractionActive = false;
    },
    deleteInteraction(name) {
      const interaction = this.getInteractionByName(name);
      if (interaction) {
        this.map.removeInteraction(interaction);
      }
    },
    clearSidebarGeometry() {
      const layer = this.getLayerById('drawingSidebarLayer');
      if (layer) {
        layer.getSource().clear();
      }
    },
    deleteSidebarGeometry() {
      this.clearSidebarGeometry();
      this.deleteInteraction('drawingSidebarInteraction');
      this.deleteInteraction('modifySidebarInteraction');
      this.deactivateCableSnapping();
      this.isModificationInteractionActive = false;
      this.isDrawingInteractionActive = false;
      this.setMinZoom(defaultMinZoom);
      this.turnOffModifySnapping();
    },
    zoomSidebarGeometry() {
      const layer = this.getLayerById('drawingSidebarLayer');
      if (!layer) {
        return;
      }
      this.fitView(layer.getSource().getExtent());
    },
    emitSidebarGeometry(e, name) {
      let sidebarFeature = e.feature;
      if (!sidebarFeature && e.features) {
        sidebarFeature = e.features.getArray()[0];
      }
      if (!sidebarFeature) {
        this.$root.$emit('addedSidebarGeometry', { inputName: name, geometry: null });
      } else {
        this.$root.$emit('addedSidebarGeometry', { inputName: name, geometry: prepareDrawnGeometry(sidebarFeature) });
      }
    },
    defaultSelectionStyle() {
      return new Style({
        fill: new Fill({
          color: 'rgba(63, 127, 191, 0.4)',
        }),
        stroke: new Stroke({
          color: 'rgba(0, 0, 255, 1)',
          width: 2,
        }),
        image: new Circle({
          radius: 5,
          stroke: new Stroke({
            color: 'rgba(0, 0, 255, 1)',
          }),
          fill: new Fill({
            color: 'rgba(63, 127, 191, 0.4)',
          }),
        }),
      });
    },
    initDrawingLayer(marker = false, isSelection = false) {
      const layer = this.getLayerById('drawingSidebarLayer');
      if (layer) {
        return;
      }
      this.map.addLayer(
        new VectorLayer({
          id: 'drawingSidebarLayer',
          isEditingLayer: true,
          source: new VectorSource(),
          style: () => {
            return [
              marker
                ? new Style({
                    image: new Icon({
                      anchor: [0.5, 43],
                      anchorXUnits: 'fraction',
                      anchorYUnits: 'pixels',
                      src: require('@/assets/images/marker2.png'),
                    }),
                  })
                : isSelection
                ? this.defaultSelectionStyle()
                : new Style({
                    fill: new Fill({
                      color: 'rgba(200,0,0,0.6)',
                    }),
                    stroke: new Stroke({
                      color: 'rgba(255,0,0,1)',
                      width: 2,
                    }),
                    image: new Circle({
                      radius: 5,
                      stroke: new Stroke({
                        color: 'rgba(0, 0, 255, 1)',
                      }),
                      fill: new Fill({
                        color: 'rgba(63, 127, 191, 0.7)',
                      }),
                    }),
                  }),
            ];
          },
          zIndex: 1000,
        })
      );
    },
    getDrawInteraction({
      geometryType,
      drawendCallback,
      geometryFunction,
      marker = false,
      condition,
      freehand = false,
      isDeactivateDrawingInteraction = true,
      layerId,
      isSelection = false,
    }) {
      const interaction = new Draw({
        source: this.getLayerById('drawingSidebarLayer').getSource(),
        type: geometryFunction ? 'Circle' : geometryType,
        condition,
        geometryFunction,
        freehand,
        style: marker
          ? new Style({
              image: new Icon({
                anchor: [0.5, 43],
                anchorXUnits: 'fraction',
                anchorYUnits: 'pixels',
                src: require('@/assets/images/marker2.png'),
              }),
            })
          : isSelection
          ? this.defaultSelectionStyle()
          : new Style({
              fill: new Fill({
                color: 'rgba(63, 127, 191, 0.7)',
              }),
              stroke: new Stroke({
                color: 'rgba(0, 0, 255, 1)',
                width: 2,
              }),
              image: new Circle({
                radius: 5,
                stroke: new Stroke({
                  color: 'rgba(0, 0, 255, 1)',
                }),
                fill: new Fill({
                  color: 'rgba(63, 127, 191, 0.7)',
                }),
              }),
            }),
      });
      interaction.set('name', 'drawingSidebarInteraction');
      interaction.on('drawend', e => {
        setTimeout(async () => {
          const cableSnappingInteraction = this.getInteractionByName('cableSnapping');
          if (cableSnappingInteraction) {
            const [firstFeature, lastFeature] = this.validateCable(e.feature);
            // Obiekt poprawnie zwalidowany
            if (firstFeature && lastFeature) {
              // Tworzymy nowy feature ze zmienioną geometrią
              // Pierwszy i ostatni punkt pochodzi z istniejących obiektów
              const preparedFeature = await this.prepareValidatedGeometry(e.feature, firstFeature, lastFeature);
              // Podmieniamy feature w eventcie
              e.feature = preparedFeature;
              drawendCallback(e);
            } else {
              // Obiekt niepoprawnie zwalidowany, usunięcie
              this.$store.set('snackbar/PUSH_ERROR!', {
                message: this.$i18n.t('errors.invalidCableGeometry'),
              });
              this.clearSidebarGeometry();
              return;
            }
          } else {
            if (layerId && this.isStreetLampDataSource(layerId)) {
              e.feature = this.roundFeaturePoint(e.feature);
            }
            drawendCallback(e);
          }
          if (isDeactivateDrawingInteraction) {
            this.deactivateCableSnapping();
            interaction.setActive(false);
            this.isDrawingInteractionActive = false;
          }
        });
      });
      return interaction;
    },
    roundFeaturePoint(feature) {
      const coordinates = feature
        .clone()
        .getGeometry()
        .getCoordinates();
      return new GeoJSON().readFeature(
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: coordinates.map(coordinate => parseFloat(coordinate.toFixed(8))),
          },
          properties: {},
        },
        {
          featureProjection: 'EPSG:2180',
          dataProjection: 'EPSG:2180',
        }
      );
    },
    deactivateCableSnapping() {
      const cableSnappingInteraction = this.getInteractionByName('cableSnapping');
      if (!cableSnappingInteraction) {
        return;
      }
      // Czyszczenie pozostałości po snapowaniu
      for (const listener of CABLE_SNAPPING_PROXY_LISTENERS) {
        unByKey(listener);
      }
      CABLE_SNAPPING_PROXY_LISTENERS = [];
      PROXY_SOURCE.clear();
      this.map.removeInteraction(cableSnappingInteraction);
    },
    validateCableSnapping(feature) {
      const geometry = feature.getGeometry();
      const [firstPoint, lastPoint] = [geometry.getFirstCoordinate(), geometry.getLastCoordinate()];
      return (
        this.validateIsPointOnAnyFeature(firstPoint, PROXY_SOURCE.getFeatures()) &&
        this.validateIsPointOnAnyFeature(lastPoint, PROXY_SOURCE.getFeatures())
      );
    },
    async prepareValidatedGeometry(feature, firstFeature, lastFeature) {
      const [rLeft, rRight] = await Promise.all([
        this.getLayerFeature({
          layer_id: firstFeature.get('layerId'),
          feature_id: firstFeature.get('featureId'),
        }),
        this.getLayerFeature({
          layer_id: lastFeature.get('layerId'),
          feature_id: lastFeature.get('featureId'),
        }),
      ]);
      const coordinates = feature
        .clone()
        .getGeometry()
        .getCoordinates();
      coordinates[0] = rLeft.geometry.coordinates;
      coordinates[coordinates.length - 1] = rRight.geometry.coordinates;
      return new GeoJSON().readFeature(
        {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates,
          },
          properties: {},
        },
        {
          featureProjection: 'EPSG:2180',
          dataProjection: 'EPSG:2180',
        }
      );
    },
    validateCable(feature) {
      const geometry = feature.getGeometry();
      const [firstPoint, lastPoint] = [geometry.getFirstCoordinate(), geometry.getLastCoordinate()];
      let firstFeature, lastFeature;
      const [xPointFirst, yPointFirst] = firstPoint.map(coordinate => coordinate.toFixed(3));
      const [xPointLast, yPointLast] = lastPoint.map(coordinate => coordinate.toFixed(3));
      for (const feature of PROXY_SOURCE.getFeatures()) {
        const [x, y] = feature
          .getGeometry()
          .getCoordinates()
          .map(coordinate => coordinate.toFixed(3));
        if (xPointFirst === x && yPointFirst === y) {
          firstFeature = feature;
        }
        if (xPointLast === x && yPointLast === y) {
          lastFeature = feature;
        }
      }
      return [firstFeature, lastFeature];
    },
    validateIsPointOnAnyFeature(point, features) {
      const [xPoint, yPoint] = point.map(coordinate => coordinate.toFixed(0));
      return features.some(feature => {
        const [x, y] = feature
          .getGeometry()
          .getCoordinates()
          .map(coordinate => coordinate.toFixed(0));
        return xPoint === x && yPoint === y;
      });
    },
    initCableSnapping(layersToSnapIds) {
      // Dla każdej warstwy dodaj proxy zapisujące ficzery z kafli do vector source
      for (const layerId of layersToSnapIds) {
        const listener = this.getLayerById(layerId, 'layers')
          .getSource()
          .on('tileloadend', evt => {
            const features = evt.tile.getFeatures();
            const featureCollection = {
              type: 'FeatureCollection',
              features: features.map(feature => {
                return {
                  type: 'Feature',
                  properties: {
                    layerId,
                    featureId: feature.getId(),
                  },
                  geometry: {
                    type: 'Point',
                    coordinates: feature.flatCoordinates_,
                  },
                };
              }),
            };
            const featuresObj = new GeoJSON().readFeatures(featureCollection, {
              featureProjection: 'EPSG:2180',
              dataProjection: 'EPSG:2180',
            });
            PROXY_SOURCE.addFeatures(featuresObj);
          });
        CABLE_SNAPPING_PROXY_LISTENERS.push(listener);
        // Odśwież warstwę żeby pobrać dane do proxy
        this.refreshMvtSource(layerId);
      }
      const snap = new Snap({
        source: PROXY_SOURCE,
      });
      snap.set('name', 'cableSnapping');
      this.map.addInteraction(snap);
    },
    handleDrawInteractionAdding({
      geometryType,
      drawendCallback = this.emitSidebarGeometry,
      geometryFunction,
      marker = false,
      condition,
      freehand = false,
      isDeactivateDrawingInteraction = true,
      layerId,
      isSelection = false,
    }) {
      const interaction = this.getInteractionByName('drawingSidebarInteraction');
      if (interaction) {
        this.map.removeInteraction(interaction);
      }
      this.map.addInteraction(
        this.getDrawInteraction({
          geometryType,
          drawendCallback,
          geometryFunction,
          marker,
          condition,
          freehand,
          isDeactivateDrawingInteraction,
          layerId,
          isSelection,
        })
      );
      this.isDrawingInteractionActive = true;
      if (layerId && this.isCableDataSource(layerId)) {
        this.handleCableSnappingAdding();
      }
    },
    getModifyInteraction({ modifyendCallback, geometryValue }) {
      const interaction = new Modify({
        source: this.getLayerById('drawingSidebarLayer').getSource(),
        style: new Style({
          image: new Circle({
            radius: 5,
            stroke: new Stroke({
              color: 'rgba(255,0,0,1)',
            }),
            fill: new Fill({
              color: 'rgba(255,0,0,0.7)',
            }),
          }),
        }),
      });
      interaction.set('name', 'modifySidebarInteraction');
      // Do zapisania ostatniej poprawnej geometrii
      const cachedGeometry = { ...geometryValue };
      interaction.on('modifyend', e => {
        setTimeout(() => {
          const cableSnappingInteraction = this.getInteractionByName('cableSnapping');
          if (cableSnappingInteraction) {
            // Obiekt poprawnie zwalidowany
            if (this.validateCableSnapping(e.features.getArray()[0])) {
              (cachedGeometry.coordinates = e.features
                .getArray()[0]
                .getGeometry()
                .getCoordinates()),
                modifyendCallback(e);
            } else {
              // Obiekt niepoprawnie zwalidowany, ustawienie na poprzedni poprawnie zwalidowany obiekt
              this.$store.set('snackbar/PUSH_ERROR!', {
                message: this.$i18n.t('errors.invalidCableGeometry'),
              });
              this.clearSidebarGeometry();
              this.addDrawingLayerFeature(cachedGeometry);
              return;
            }
          } else {
            // Zwykły callback w przypadku braku snapowania
            modifyendCallback(e);
          }
        });
      });
      return interaction;
    },
    handleModifyInteractionAdding({
      modifyendCallback = this.emitSidebarGeometry,
      layerId,
      geometryValue,
      isInitSnapping = true,
    }) {
      const interaction = this.getInteractionByName('modifySidebarInteraction');
      if (interaction) {
        this.map.removeInteraction(interaction);
      }
      this.map.addInteraction(this.getModifyInteraction({ modifyendCallback, geometryValue }));
      this.isModificationInteractionActive = true;
      if (layerId && this.isCableDataSource(layerId)) {
        this.handleCableSnappingAdding();
      } else if (isInitSnapping) {
        this.initModifySnapping();
      }
    },
    isCableDataSource(layerId) {
      return this.getLayerById(layerId, 'layers').get('data_source_name') === this.cableDataSourceName;
    },
    isStreetLampDataSource(layerId) {
      return this.getLayerById(layerId, 'layers')?.get('data_source_name') === this.streetLampDataSourceName;
    },
    handleCableSnappingAdding() {
      const layersToSnapIds = this.getLayerById('layers')
        .getLayers()
        .getArray()
        .filter(layer =>
          ['zdm_data_cabinet', 'zdm_data_street_lamp', 'zdm_data_power_station'].includes(layer.get('data_source_name'))
        )
        .map(layer => layer.get('id'));
      this.initCableSnapping(layersToSnapIds);
    },
    initModifySidebarGeometry({ geometryValue, modifyendCallback, layerId, isInitSnapping }) {
      this.initDrawingLayer();
      this.handleModifyInteractionAdding({ modifyendCallback, layerId, geometryValue, isInitSnapping });
      if (geometryValue) {
        this.addDrawingLayerFeature(geometryValue);
      }
    },
    initDrawingSidebarGeometry({
      geometryType,
      geometryValue,
      withEditing = false,
      drawendCallback,
      modifyendCallback,
      geometryFunction,
      marker = false,
      freehand = false,
      isDeactivateDrawingInteraction = true,
      condition,
      layerId,
      isInitSnapping = true,
      isSelection = false,
    }) {
      this.$root.$emit('deactivateAllTools');
      const dict = {
        point: 'Point',
        multipoint: 'MultiPoint',
        line: 'LineString',
        linestring: 'LineString',
        multilinestring: 'MultiLineString',
        polygon: 'Polygon',
        multipolygon: 'MultiPolygon',
      };
      this.initDrawingLayer(marker, isSelection);
      this.handleDrawInteractionAdding({
        geometryType: dict[geometryType] || geometryType,
        drawendCallback,
        geometryFunction,
        marker,
        condition,
        freehand,
        isDeactivateDrawingInteraction,
        layerId,
        isSelection,
      });
      if (isInitSnapping) {
        this.initModifySnapping();
      }
      if (withEditing) {
        this.handleModifyInteractionAdding({
          modifyendCallback: modifyendCallback || drawendCallback,
          layerId,
          isInitSnapping: false,
        });
      }
      if (geometryValue) {
        this.addDrawingLayerFeature(geometryValue);
      }
    },
    addDrawingLayerFeature(geometry) {
      const layer = this.getLayerById('drawingSidebarLayer');
      const feature = new GeoJSON().readFeature(geometry, {
        featureProjection: defaultEpsg || 'EPSG:4326',
        dataProjection: geometry.crs.properties.name || 'EPSG:4326',
      });
      layer.getSource().addFeature(feature);
    },
  },
  mounted() {
    this.registerSidebarGeometryListeners();
  },
};
